
import { defineComponent, ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import StarXpGate from '@/components/star/StarXpGate.vue'
import { isXpLowVersion } from '@/utils/xpEnv'
import StarTabBar from '@/components/star/StarTabBar.vue'
import ResultPanel from '@/views/star/result.vue'
import starApi from '@/api/starApi'
import { useStarTheme, useStarRouter } from '@/utils/useStarTheme'
import StrategyCard from '@/components/star/StrategyCard.vue'
import StarTipDialog from '@/components/star/StarTipDialog.vue'
import StarActivationDialog from '@/components/star/StarActivationDialog.vue'
import type { StrategyItem } from '@/components/star/StrategyCard.vue'
import { checkStarPageFree } from '@/utils/useStarPageFree'
import { updateStarTrialRemainCache, useStarActivation } from '@/utils/useStarActivation'
import { useStarTip } from '@/utils/useStarTip'
const filterTabs = [
  { label: '全部', value: 'all' },
  { label: '已订阅', value: 'sub' }
]

// 生成模拟图表数据（保留供其他地方使用）
function generateMockChart(events: { date: string; eventType: string }[]) {
  const points: { date: string; strategy: number; benchmark: number; eventType: any }[] = []
  let strategy = 0
  let benchmark = 0
  const eventMap = Object.fromEntries(events.map(e => [e.date, e.eventType]))
  for (let i = 0; i < 120; i++) {
    const d = new Date(2024, 7, 1)
    d.setDate(d.getDate() + i * 2)
    const date = d.toISOString().slice(0, 10).replace(/-/g, '')
    strategy += (Math.random() - 0.45) * 2
    benchmark += (Math.random() - 0.48) * 1
    points.push({
      date,
      strategy: parseFloat(strategy.toFixed(2)),
      benchmark: parseFloat(benchmark.toFixed(2)),
      eventType: eventMap[date] as any
    })
  }
  return points
}

export default defineComponent({
  name: 'StarIndex',
  components: { StarXpGate, StarTabBar, StrategyCard, StarTipDialog, StarActivationDialog, ResultPanel },
  setup() {
    const route = useRoute()
    const store = useStore()
    const { isLight, colors } = useStarTheme()
    const pageStyle = computed(() => ({
      background: colors.value.bg,
      '--filter-tab-color': isLight.value ? '#666666' : 'rgba(255,255,255,0.45)',
      '--filter-tab-hover-color': isLight.value ? '#333333' : 'rgba(255,255,255,0.8)',
      '--create-wrap-bg': isLight.value ? 'transparent' : 'linear-gradient(180deg, #252525 0%, #262626 100%)',
      '--create-wrap-border': isLight.value ? '1px solid #dbdfe6' : 'none',
      '--create-wrap-shadow': isLight.value ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
    }))
    const { starPush } = useStarRouter()
    const starUserName = computed(
      () => store.state.userInfo?.userName || (route.query.username as string) || (route.query.userName as string) || ''
    )
    const {
      showActivationDialog,
      showActivationQr,
      ensureStarActivated,
      openActivationQr,
      refreshActivation,
      hideActivation,
    } = useStarActivation()

    // 将当前路由参数透传到对话框页面
    const openDialog = (path: string, title: string, width = 800, height = 600) => {
      const origin = document.location.origin
      const query = new URLSearchParams(route.query as Record<string, string>).toString()
      const url = `${origin}${document.location.pathname}#${path}${query ? '?' + query : ''}`
      window.open(`gw://webkitdlg?width=${width};height=${height};mode=0;title=${title};allowdestroy=1;url=${url}`)
    }

    const activeTopTab = ref('home')
    const activeFilter = ref('all')
    const activePanel = ref<'result' | 'message' | null>(null)
    const openPanel = (panel: 'result' | 'message') => { activePanel.value = panel }
    const closePanel = () => { activePanel.value = null }
    const loading = ref(false)
    const list = ref<StrategyItem[]>([])
    const showLoginMask = ref(false)
    const showTokenDialog = ref(false)
    const tokenInput = ref('')

    watch(showTokenDialog, (open) => {
      if (!open) tokenInput.value = ''
    })

    const {
      tipDialog, unlock, showTip,
      handleUnlockClose, handleUnlockBannerClick, handleUnlockShareClose, handleUnlockPromoClose,
      handleUnlockPurchaseClick,
    } = useStarTip()

    const summary = reactive({
      totalCount: null as number | null,
      avgWinRate: null as string | null,
      avgReturn: null as string | null
    })

    const actions = [
      {
        key: 'stock',
        name: '个股量化',
        desc: '买卖一目了然',
        iconBg: '#2979ff',
        icon: require('@/assets/star/icon-stock.png'),
        handler: () => {
          starPush('/star/stock', { isfrom: '1' })
        }
      },
      {
        key: 'group',
        name: '组合量化',
        desc: '策略验证优化',
        iconBg: '#ff6d00',
        icon: require('@/assets/star/icon-group.png'),
        handler: () => {
          starPush('/star/group')
        }
      },
      {
        key: 'token',
        name: '策略口令粘贴',
        desc: '导入高手策略',
        iconBg: '#00bcd4',
        icon: require('@/assets/star/icon-token.png'),
        handler: () => {
          showTokenDialog.value = true
        }
      },
      {
        key: 'result',
        name: '回测记录',
        desc: '历史回测记录',
        iconBg: '#ff6d00',
        icon: require('@/assets/star/icon-result.png'),
        handler: () => openPanel('result')
      }
    ]

    const fetchList = async (from: string) => {
      loading.value = true
      try {
        const token = route.query.token as string
        const userName = store.state.userInfo?.userName
        const res = await starApi.getStrategyList({
          data: { from },
          headers: { token, userName }
        })
        const raw = res?.data?.list || []
        const data = res?.data || {}

        updateStarTrialRemainCache(route, userName, data.testNumRemain)

        // 同步更新 summary
        summary.totalCount = data.listTotal ?? null
        summary.avgWinRate = data.winRate != null ? (data.winRate * 100).toFixed(2) + '%' : null
        summary.avgReturn = data.totalReturn != null
          ? (Math.trunc(data.totalReturn * 100 * 100) / 100).toFixed(2) + '%'
          : null

        const actionMap: Record<number, string> = { 1: 'subscribe', 2: 'follow', 3: 'interrupted' }
        list.value = raw.map((item: any) => {
          const type = item.quantType === 3 || item.quantType === 4 ? 'group' : 'stock'
          let followStatus: 'following' | 'interrupted' | null = null
          if (item.isFollowing) followStatus = 'following'
          let subscribeStatus: 'following' | 'interrupted' | 'subscribed' | null = null
          if (item.isFollowing) subscribeStatus = 'following'
          else if (item.isSubscribed) subscribeStatus = 'subscribed'
          const chartData = (item.returnList || []).map((p: any) => ({
            date: String(p.d),
            strategy: (p.v ?? 0) * 100,
            benchmark: (p.b ?? 0) * 100,
            eventType: p.a ? actionMap[p.a] : undefined
          }))
          return {
            id: item.taskID,
            name: item.title,
            type,
            quantType: item.quantType,
            followStatus,
            subscribeStatus,
            strategyReturn: item.totalReturn != null ? item.totalReturn * 100 : null,
            maxDrawdown: item.riskRatio != null ? item.riskRatio * 100 : 0,
            profitLossRatio: item.riskRatio ?? null,
            winRate: item.winRate,
            rightTag: item.rightTag,
            leftTags: item.leftTags,
            chartData,
            noBuySell: !!item.noBuySell
          }
        })
      } catch (e) {
        list.value = []
      } finally {
        loading.value = false
      }
    }

    // 权限初始化入口
    const initAuth = async () => {
      const token = route.query.token as string

      if (!token) {
        showLoginMask.value = true
        console.log('gw://OnUserLogin')
        window.open('gw://OnUserLogin')
        return
      }

      // 从路由参数直接取 userName 和 userType，不再调 tokenLogin
      const userName = route.query.username as string
      const userType = route.query.userType as string

      if (!userName) {
        showLoginMask.value = true
        console.log('gw://OnUserLogin')
        window.open('gw://OnUserLogin')
        return
      }

      store.commit('setUserInfo', { userName, userType })
      checkStarPageFree('home', route)
      fetchList(activeFilter.value)
    }

    const switchFilter = (value: string) => {
      if (activeFilter.value === value) return
      activeFilter.value = value
      fetchList(value)
    }

    const confirmToken = async () => {
      const word = tokenInput.value.trim()
      if (!word) return
      if (!(await ensureStarActivated(route, starUserName.value))) return

      const token = route.query.token as string
      const userName = store.state.userInfo?.userName

      try {
        const res = await starApi.redeemWatchword({
          data: { watchWord: word },
          headers: { token, userName }
        })

        // status: 1 正常，2 兑换故障；优先判断 status
        if (res?.data?.status === 2) {
          showTip({ title: '提示', content: res?.data?.toast || '口令无效，请重新输入' })
          return
        }

        // 兜底：接口本身报错（无 data 或其他异常 code）
        if (!res?.data) {
          showTip({ title: '提示', content: res?.message || '口令无效，请检查后重试' })
          return
        }

        showTokenDialog.value = false
        starPush('/star/group', { isPassword: 'true', watchWord: word })
      } catch (e) {
        showTip({ title: '提示', content: '口令兑换失败，请稍后重试' })
      }
    }

    const handleFollow = (item: StrategyItem) => {
      console.log('一键跟投', item)
    }

    const handleLive = (item: StrategyItem) => {
      console.log('去实盘查看', item)
    }

    const handleCreate = () => {
      starPush('/star/stock')
    }

    onMounted(() => {
      if (isXpLowVersion(route.query as Record<string, unknown>)) return
      initAuth()
    })

    return {
      isLight, colors, pageStyle,
      activeTopTab, activeFilter, filterTabs,
      activePanel, openPanel, closePanel,
      loading, list, summary, actions, showLoginMask,
      showTokenDialog, tokenInput,
      tipDialog, unlock,
      handleUnlockClose, handleUnlockBannerClick, handleUnlockShareClose, handleUnlockPromoClose,
      handleUnlockPurchaseClick,
      showActivationDialog, showActivationQr, openActivationQr, refreshActivation, hideActivation, route, starUserName,
      switchFilter, confirmToken, handleFollow, handleLive, handleCreate, openDialog
    }
  }
})
